import { useState, useMemo, useEffect } from 'react';

import type { GridPaginationModel } from '@mui/x-data-grid';

import { useGetPaginatedWaiverDocumentsQuery } from '@/gql/operations';

import type { ParsedIManageDocument } from '../useParseRecords';

const DEFAULT_PAGE_SIZE = 50;

export const usePaginatedWaiverDocuments = (waiverId: string | undefined, searchTerm?: string) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Reset to first page when search term changes
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchTerm]);

  const where = useMemo(() => {
    if (!searchTerm?.trim()) return undefined;
    const pattern = `%${searchTerm.trim()}%`;
    return {
      _or: [{ name: { _ilike: pattern } }, { doc_id: { _ilike: pattern } }],
    };
  }, [searchTerm]);

  const { data, loading, previousData } = useGetPaginatedWaiverDocumentsQuery({
    variables: {
      waiverRecordId: waiverId!,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
      where,
    },
    skip: !waiverId,
  });

  const activeData = data ?? previousData;
  const waiverRecord = activeData?.clm_waiver_records[0];
  const docs = waiverRecord?.waiver_imanage_documents ?? [];

  const rows: ParsedIManageDocument[] = useMemo(
    () =>
      docs.map((doc) => ({
        id: doc.id,
        docId: doc.doc_id ?? 'N/A',
        docName: doc.name ?? doc.doc_id ?? 'Unknown Document',
      })),
    [docs],
  );

  // Total count of matching records, independent of the current page.
  const rowCount = waiverRecord?.waiver_imanage_documents_aggregate?.aggregate?.count ?? 0;

  return {
    rows,
    rowCount,
    paginationModel,
    setPaginationModel,
    loading,
  };
};
